import { useMemo } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import '@fontsource-variable/source-sans-3/index.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import { createWorkspaceTheme, focusRing } from './createWorkspaceTheme';

/**
 * The theme boundary for Decision Workspace screens (DESIGN.md).
 *
 * Wrap only the screens that have adopted the workspace patterns. Ported screens keep
 * `PlatformThemeProvider` until the owner approves their replacement pattern.
 */
export function WorkspaceThemeProvider({
  children,
  baseline = true,
  focusRing: ring = true,
}: {
  children: ReactNode;
  /**
   * Apply the workspace page baseline (body background, colour scheme). Turn it OFF when the
   * provider is nested inside a page that already has one, e.g. a workspace screen shown inside
   * the Classic shell during migration: a nested baseline would restyle the whole document.
   */
  baseline?: boolean;
  /**
   * Draw the workspace focus ring on every control inside. Turn it OFF when the provider wraps
   * content that is not a workspace screen (ported originals inside the workspace shell), whose
   * focus styling must stay as it is.
   */
  focusRing?: boolean;
}) {
  const theme = useMemo(() => createWorkspaceTheme(), []);
  return (
    <ThemeProvider theme={theme}>
      {baseline ? <CssBaseline enableColorScheme /> : null}
      {ring ? (
        // The focus ring has to reach every control inside, including when the baseline is off
        // (a workspace screen inside the Classic shell): the baseline's global rule is not there
        // then, and a global rule would restyle the Classic screens around it. `display: contents`
        // keeps this element out of the layout.
        <Box sx={{ display: 'contents', '& :focus-visible': focusRing }}>{children}</Box>
      ) : (
        children
      )}
    </ThemeProvider>
  );
}
